import type { Finding } from './api.ts'

/**
 * Which ativo an operational rule finding is about (#549).
 *
 * A rule about a particular ativo -- a dose outside the usual, an excipient
 * that does not suit the ativo's class -- is shown on that ativo's row rather
 * than in a section of its own, where the conferente had to carry the name
 * back up to the board to find the line.
 *
 * Matched from what the finding already stores, the ativo's name as keyed and
 * its product code, so findings scored before this need no re-scoring. A rule
 * whose ativo is on no row matches nothing and keeps its place in the rules
 * section: an apontamento must never be dropped for want of somewhere to go.
 */

/**
 * The ativos a rule finding is about: `ingredients`, which every rule about
 * ativos records, or the single `ingredient` the dose rule stored before it.
 */
export function ruleIngredients(evidence: Record<string, unknown>): readonly string[] {
  const many = evidence['ingredients']
  if (Array.isArray(many)) {
    return many.filter((one): one is string => typeof one === 'string' && one.trim() !== '')
  }
  const one = evidence['ingredient']
  return typeof one === 'string' && one.trim() !== '' ? [one] : []
}

const sameName = (a: string, b: string) =>
  a.trim().toLocaleUpperCase('pt-BR') === b.trim().toLocaleUpperCase('pt-BR')

interface KeyedLine {
  readonly rawName: string
  readonly erpCode?: string | undefined
}

/**
 * The keyed lines an operational finding is about, as indexes into `lines`.
 *
 * One line for each ativo the rule names, and the first where an ativo was
 * keyed twice: a dose rule that fired for one of two MINOXIDIL lines stores
 * nothing to say which, and drawn under both it would claim both doses.
 */
export function linesOfRule(finding: Finding, lines: readonly KeyedLine[]): ReadonlySet<number> {
  if (finding.check !== 'operational') return new Set()
  const evidence = (finding.evidence ?? {}) as Record<string, unknown>
  const names = ruleIngredients(evidence)
  const found = names
    .map((name) => lines.findIndex((line) => sameName(name, line.rawName)))
    .filter((index) => index !== -1)
  if (found.length > 0) return new Set(found)
  // `erpCode` is recorded only by the rules about exactly one ativo, so with
  // several names it would say nothing about which of them a line is.
  const code = evidence['erpCode']
  if (names.length !== 1 || typeof code !== 'string' || code === '') return new Set()
  const byCode = lines.findIndex((line) => line.erpCode === code)
  return byCode === -1 ? new Set() : new Set([byCode])
}

/**
 * The operational findings the ativos board shows on its rows, by
 * fingerprint, so the rules section does not list them a second time.
 */
export function rulesOnActives(
  rows: readonly { readonly findings: readonly Finding[]; readonly notes: readonly Finding[] }[],
): ReadonlySet<string> {
  return new Set(
    rows.flatMap((row) =>
      [...row.findings, ...row.notes]
        .filter((f) => f.check === 'operational')
        .map((f) => f.fingerprint),
    ),
  )
}

/** A rule's own enunciado, as it was titled when it ran. */
export function ruleTitle(finding: Finding): string | undefined {
  const title = (finding.evidence as Record<string, unknown> | undefined)?.['title']
  return typeof title === 'string' && title.trim() !== '' ? title.trim() : undefined
}

/**
 * What a rule finding says, for the tooltip of the row it sits on.
 *
 * The rule's own enunciado leads: on an ativo's row nothing else on screen
 * says a rule is what flagged it. The sentence about this requisição follows
 * when one was written.
 */
export function ruleReason(finding: Finding, sentence: string | undefined): string | undefined {
  const parts = [ruleTitle(finding) ?? '', sentence?.trim() ?? ''].filter((part) => part !== '')
  return parts.length === 0 ? undefined : parts.join(' — ')
}

/**
 * What a rule's ⓘ says: the sentence written about this requisição, or, with
 * none, what kind of apontamento it is. Never "os dois lados discordam", the
 * words of a comparison -- a rule has no two sides.
 */
const RULE_ASKS = 'Regra operacional: a regra apontou um problema'
const RULE_REMINDS = 'Lembrete de regra operacional: não exige resposta'

export function ruleHint(finding: Finding, sentence: string | undefined): string {
  const written = sentence?.trim()
  if (written !== undefined && written !== '') return written
  return finding.disposition === 'info' || finding.disposition === 'ok' ? RULE_REMINDS : RULE_ASKS
}
